import { absoluteValue, createBigDecimal, plus } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { hyperliquidAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { parseHyperliquidActionEvidence } from '../parseActionEvidence'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'POST',
    path: '/info',
    body: {
      type: 'userFunding',
      startTime: from,
      ...(to ? { endTime: to } : {}),
    },
    responseSchema,
  })

  // 昇順 sort by time
  const sortedFundings = response.sort((a, b) => a.time - b.time)

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // Group items by coin and time
  const groupedFundings = new Map<string, typeof sortedFundings>()

  for (const item of sortedFundings) {
    // Check if item type is supported
    if (item.delta.type !== 'funding') {
      throw new Error(`Unsupported funding type: ${item.delta.type}`)
    }

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? item.time : Math.min(fetchedFrom, item.time)
    fetchedTo =
      fetchedTo === undefined ? item.time : Math.max(fetchedTo, item.time)

    // Group by coin and time
    const groupKey = `${item.delta.coin}_${item.time}`
    const group = groupedFundings.get(groupKey) || []
    group.push(item)
    groupedFundings.set(groupKey, group)
  }

  // Process grouped fundings
  for (const fundings of groupedFundings.values()) {
    const firstFunding = fundings[0]
    const asset = getAsset('USDC')
    if (!asset) {
      throw new AssetNotFoundException('USDC')
    }

    // Calculate total amount
    const amount = fundings.reduce(
      (acc, curr) => plus(acc, curr.delta.usdc),
      createBigDecimal(0n),
    )

    // Create action only if amount is not zero
    if (amount.value !== 0n) {
      const comment = `Funding of ${firstFunding.delta.coin}/USDC`
      const source = `${hyperliquidAccountCategory}_${config.type}_${firstFunding.delta.coin}_${firstFunding.time}`
      const type = amount.value > 0n ? 'derivative-profit' : 'derivative-fee'
      const absoluteAmount = absoluteValue(amount)

      actions.push({
        comment,
        evidence: 'system-rule',
        order: 0,
        source,
        type,
        timestamp: firstFunding.time,
        transfers: [
          {
            asset,
            amount: absoluteAmount,
            direction: amount.value > 0n ? 'in' : 'out',
          },
        ],
      })
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions.map((action) =>
      parseHyperliquidActionEvidence(action),
    ),
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
